import React, { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { connect } from 'react-redux';
import AuthRouterView from './auth-view';
import { setDocumentTitle } from '../../utils/utils';
import useApiClient from '../../hooks/use-api-client';
import LocalStorageService from '../../utils/local-storage-service';
import { getMatchingUser, hasAuthorizedRole } from '../../utils/auth';
import { setRoles, loginSuccess } from '../../state/actions/users';
import useLogout from '../../hooks/use-logout';
import ZApiClient from '../../utils/client/z-api-client';

const LocalStorageClient = new LocalStorageService();

// getCookieValue reads the admin-specific user_session cookie (user_session_admin_<env>) and falls
// back to the generic one, matching how travel-service names the cookie per app/environment.
const getCookieValue = (name) => {
  if (typeof document === 'undefined') return undefined;
  const env = process.env.REACT_APP_STAGE || 'prod';
  const parse = (raw) => {
    try {
      return JSON.parse(decodeURIComponent(raw));
    } catch (error) {
      return decodeURIComponent(raw);
    }
  };
  const adminMatch = document.cookie.match(new RegExp(`(^| )${name}_admin_${env}=([^;]+)`));
  if (adminMatch) return parse(adminMatch[2]);
  const match = document.cookie.match(new RegExp(`(^| )${name}=([^;]+)`));
  if (match) return parse(match[2]);
  return undefined;
};

// On localhost the user_session cookie cannot be set (it is scoped to .zicasso.com), so local dev
// has to keep relying on the localStorage token.
const isLocalhost = typeof window !== 'undefined'
  && /^(localhost|127\.0\.0\.1)$/.test(window.location.hostname);

// setInternalTrafficCookie tags this team-member device as internal traffic client-side, as a
// backup to the server-side cookie set by travel-service on login.
const setInternalTrafficCookie = () => {
  if (typeof document === 'undefined' || typeof window === 'undefined') return;
  const domain = window.location.hostname.endsWith('zicasso.com') ? '; domain=.zicasso.com' : '';
  const secure = window.location.protocol === 'https:' ? '; Secure' : '';
  const oneYear = 365 * 24 * 60 * 60;
  document.cookie = `zicasso_internal=true; path=/; max-age=${oneYear}; SameSite=Lax${domain}${secure}`;
};

// getAccessToken requires the user_session cookie: if it is missing the team member is logged out
// and forced to re-login, which re-issues the internal-traffic cookie. The localStorage token is
// only used as a complement once the cookie is confirmed present, except on localhost where the
// cookie cannot be set and local dev keeps relying on the localStorage token.
const getAccessToken = () => {
  const userSessionCookie = getCookieValue('user_session');
  const cookieSessionId = userSessionCookie?.personType === 'teamMember'
    ? userSessionCookie?.sessionId
    : undefined;

  const localStorageAccessToken = LocalStorageClient.getFromLocalStorage('accessToken');

  if (!cookieSessionId) {
    return isLocalhost ? localStorageAccessToken : undefined;
  }
  return localStorageAccessToken || cookieSessionId;
};

const AuthRouterRedux = ({
  children,
  loading,
  loggedIn,
  loginModal,
  readOnly,
  loginSuccess,
  dispatch
}) => {
  const apiClient = useApiClient();
  const location = useLocation();
  const [setLogoutNow] = useLogout(dispatch, false);
  
  useEffect(() => {
    const accessToken = getAccessToken();
    const isAuthorized = () => {
      if (accessToken) {
        setInternalTrafficCookie();
        // Check token status and get user
        apiClient.authClient
        .getToken(accessToken)
        .then((data) => apiClient.peopleClient.getTeamMember(data.email))
        .then((data) => {
          const matchingUser = getMatchingUser(data.email, data.userInfo);
          if (!matchingUser || !hasAuthorizedRole(matchingUser.roles)) {
            throw new Error('Not Authorized');
          }
            ZApiClient.setApiClientToken(accessToken);
            setRoles(matchingUser.roles);
            loginSuccess(data.email);
          })
          .catch((e) => {
            console.log(e);
            setLogoutNow(true);
          });
      } else {
        setLogoutNow(true);
      }
    };

    isAuthorized();
  }, []);

  useEffect(() => {
    setDocumentTitle(location.pathname);
  }, [location.pathname]);

  return (
    <AuthRouterView
    loggedIn={loggedIn}
    loading={loading}
    loginModal={loginModal}
    readOnly={readOnly}
    >
      {children}
    </AuthRouterView>
  )
}

const mapStateToProps = (state) => ({
  loggedIn: state.users.login.loggedIn,
  loading: state.users.login.isFetching,
  loginModal: state.modals.loginModal || {},
  readOnly: state.versions.readOnly,
});

const mapDispatchToProps = (dispatch) => ({
  loginSuccess: (username) => dispatch(loginSuccess(username)),
  setRoles: (roles) => dispatch(setRoles(roles)),
  dispatch,
});

export default connect(mapStateToProps, mapDispatchToProps)(AuthRouterRedux);
